/// <reference types="vite/client" />
/**
 * Boli Live - Web Application Entry Point
 *
 * Phase 1: Web MVP
 * Phase 2: Same core logic will work on React Native
 */

import React from 'react';
import ReactDOM from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter } from 'react-router-dom';
import { initializeBoliCore } from '@boli/core';
import App from './App';
import { Toaster } from './components/ui/toaster';
import { createRealtimeClient } from './lib/adapters/pocketbase-realtime';
import './styles/globals.css';

// Initialize React Query client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5000,
      retry: 2,
    },
  },
});

// Web-specific secure storage adapter
const webSecureStorage = {
  async setItem(key: string, value: string): Promise<void> {
    // In production, use encrypted localStorage or IndexedDB
    localStorage.setItem(key, value);
  },
  async getItem(key: string): Promise<string | null> {
    return localStorage.getItem(key);
  },
  async removeItem(key: string): Promise<void> {
    localStorage.removeItem(key);
  },
  async clear(): Promise<void> {
    const boliKeys = Object.keys(localStorage).filter((k) => k.startsWith('boli_'));
    boliKeys.forEach((k) => localStorage.removeItem(k));
  },
};

// Web-specific fetch adapter
const webFetchAdapter = async (url: string, options?: RequestInit): Promise<Response> => {
  // Note: Using 'same-origin' instead of 'include' because PocketBase CORS returns '*'
  // which is incompatible with credentials. Auth token is sent via Authorization header.
  return fetch(url, {
    ...options,
    credentials: 'same-origin',
  });
};

// Initialize Boli Core
const initApp = async () => {
  const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8090';
  const googleClientId = import.meta.env.VITE_GOOGLE_CLIENT_ID || '';

  // Create PocketBase realtime client for WebSocket subscriptions
  const { realtimeAdapter } = createRealtimeClient(apiBaseUrl);

  try {
    await initializeBoliCore({
      apiBaseUrl,
      googleClientId,
      fetchAdapter: webFetchAdapter,
      secureStorage: webSecureStorage,
      realtimeAdapter, // Enable real-time bid updates via WebSocket
    });
    console.log('Boli Core initialized for web with realtime support');
  } catch (error) {
    console.error('Failed to initialize Boli Core:', error);
  }
};

// Initialize and render
initApp().then(() => {
  ReactDOM.createRoot(document.getElementById('root')!).render(
    <React.StrictMode>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <App />
          <Toaster />
        </BrowserRouter>
      </QueryClientProvider>
    </React.StrictMode>
  );
});
